import { queryCommonFlags } from "@/api/constants";
import {
  TOAST_IDS,
  ToastType,
  UploadedFile,
  DeveloperTrackItem,
  EnvironmentVariables,
} from "@/modules/common/types";
import { AttachmentMetadata } from "@/modules/common/dtos/index.v2";
import { showToast } from "@/modules/common/utils";
import { getFormFields } from "@/modules/lead-page/api/lead-page.v2.api";
import { queryClient } from "@/queryClient";
import { useState, useMemo, useCallback, useContext, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { SalesStatuses, SaleTypeNames } from "@/modules/sales/constants";
import { normalizeStatusName } from "@/modules/lead-page/utils/status-name";
import { getTracks } from "@/modules/property-tracks/api";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import {
  transformErealtyPayload,
  transformFormData,
} from "@/modules/lead-page/utils";
import { getDevelopers } from "@/modules/leads/api/index.v2";
import { useAnalyticsContext } from "@/modules/analytics/context/analytics.context";
import { MixpanelTrackingUtil } from "@/modules/analytics/utils/mixpanel-tracking.util";
import useAuth from "@/modules/common/hooks/auth.hook";

const MAX_TOTAL_SIZE_MB = 10;
const MAX_ATTACHMENT_FILES = 3;
const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

type SaleType =
  | "Blocked"
  | "Reserved"
  | "EOI"
  | "Contracted"
  | "Promise"
  | null;
const useEditStatusForm = ({
  status,
  handleConfirmUpdate,
}: {
  status: {
    state: string | null;
    statusName: string;
    editLeadStatusModalOpen: boolean;
    setEditLeadStatusModalOpen: (value: boolean) => void;
    data?: { data: any[] } | null;
  };
  handleConfirmUpdate?: (values: any) => Promise<void>;
}) => {
  const { lead } = useContext(LeadPageContext);
  const { analytics, userInfo } = useAnalyticsContext();
  const { getTokenByKey } = useAuth();
  const journeyUId = lead?.data?.lead?.journey_uid;

  const [statusFormUId, setStatusFormUId] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [paymentFiles, setPaymentFiles] = useState<File[]>([]);
  const [paymentPlanFiles, setPaymentPlanFiles] = useState<File[]>([]);
  const [selectedTrackUId, setSelectedTrackUId] = useState<string | null>(null);
  const [showTrackSelectionModal, setShowTrackSelectionModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<{
    values: any;
    formType?: "form" | "sale";
    editedSaleData?: { saleUId: string; leadUId: string };
  } | null>(null);

  const [erealtyIframeOpenCommentModal, setErealtyIframeOpenCommentModal] =
    useState(false);
  const [erealtyIframeComment, setErealtyIframeComment] = useState("");
  const [erealtyIframePayload, setErealtyIframePayload] = useState<any>(null);

  // Analytics tracking for eRealty events
  const showResultsEventTimestampRef = useRef<number | null>(null);
  const filtersSubmitEventTimestampRef = useRef<number | null>(null);
  const [clickedShowResults, setClickedShowResults] = useState(false);

  const [createdTracks, setCreatedTracks] = useState<DeveloperTrackItem[]>([]);
  const [searchDevelopers, setSearchDevelopers] = useState("");

  const handleAddTrack = useCallback(() => {
    setCreatedTracks((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        leadUId: lead?.data?.lead?.uid || "",
        developerUId: "",
        developerUserUId: "",
      },
    ]);
    setSearchDevelopers("");
  }, [lead?.data?.lead?.uid]);

  const handleDeleteTrack = useCallback((id: string) => {
    setCreatedTracks((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleTrackChange = useCallback(
    (
      id: string,
      developerUId: string,
      developerUserUId: string,
      developerName?: string,
      developerLogoPath?: string,
      agentName?: string
    ) => {
      setCreatedTracks((prev) =>
        prev.map((t) =>
          t.id === id
            ? {
                ...t,
                developerUId,
                developerUserUId,
                developerName,
                developerLogoPath,
                agentName,
              }
            : t
        )
      );
    },
    []
  );

  // Check if the selected status has effectiveOnPropertyTrack enabled
  const selectedStatus = status.data?.data?.find(
    (s: any) => s.uid === status.state
  );

  useEffect(() => {
    const erealtyOrigin = new URL(String(EnvironmentVariables.EREALTY_URL)).origin;

    const handleMessage = (event: MessageEvent) => {
      // Validate the origin of the message
      if (event.origin !== erealtyOrigin) return;

      // Handle iframe ready for authentication
      if (event.data?.type === "EREALTY_IFRAME_READY") {
        // Retrieve the current token from storage
        const accessToken = getTokenByKey("accessToken");

        if (accessToken && event.source) {
          (event.source as Window).postMessage(
            {
              type: "AUTH_TOKEN",
              token: accessToken,
            },
            erealtyOrigin
          );
        }
      }

      if (event.data?.type === "CRM_FILTERS_SUBMIT") {
        setErealtyIframePayload(event.data.payload);
        setErealtyIframeOpenCommentModal(true);

        // Capture event timestamp when filters are submitted
        filtersSubmitEventTimestampRef.current = event.timeStamp;
      }

      if (event.data?.type === "CRM_SHOW_RESULTS") {
        // Track the event timestamp when user clicks show results (first click only)
        if (showResultsEventTimestampRef.current === null) {
          showResultsEventTimestampRef.current = event.timeStamp;
        }
        setClickedShowResults(true);
      }
    };

    if (selectedStatus?.erealtyForm) {
      window.addEventListener("message", handleMessage);
    }
    return () => {
      window.removeEventListener("message", handleMessage);
      // Reset tracking state when modal closes
      if (!selectedStatus?.erealtyForm) {
        showResultsEventTimestampRef.current = null;
        filtersSubmitEventTimestampRef.current = null;
        setClickedShowResults(false);
      }
    };
  }, [selectedStatus, getTokenByKey]);

  const {
    data: formFields,
    isLoading: queryIsLoading,
    fetchStatus,
  } = useQuery(
    ["getStatusForm", statusFormUId],
    () => (statusFormUId ? getFormFields(statusFormUId) : null),
    {
      enabled: statusFormUId !== undefined && !selectedStatus?.erealtyForm,
      ...queryCommonFlags,
      retry: false,
    }
  );

  const { data: developersData, isLoading: isLoadingDevelopers } = useQuery({
    queryKey: ["developers", searchDevelopers],
    queryFn: () =>
      getDevelopers(searchDevelopers ? { name: searchDevelopers } : {}),
    ...queryCommonFlags,
    enabled: selectedStatus?.createDeveloperTrack,
  });

  const developers = developersData?.data?.results || [];

  const isEffectiveOnPropertyTrack =
    selectedStatus?.effectiveOnPropertyTrack === true;

  const {
    data: tracksData,
    isLoading: isLoadingTracks,
    isFetching: isFetchingTracks,
    isError: isTracksError,
    isSuccess: isTracksSuccess,
    refetch: refetchTracks,
  } = useQuery({
    queryKey: ["getTracksForJourney", journeyUId],
    queryFn: async () => {
      const response = await getTracks({
        filters: {
          journeyUId: journeyUId!,
        },
        pagination: {
          page: 1,
          pageSize: 100,
          orderBy: "createdAt",
          orderDirection: "DESC",
        },
      });
      // Return only this journey's tracks
      // We'll let the user choose which track to use for the status update
      const allTracks = response.data?.results || [];

      return {
        ...response,
        data: {
          ...response.data,
          results: allTracks,
        },
      };
    },
    ...queryCommonFlags,
    enabled:
      !!journeyUId &&
      status.editLeadStatusModalOpen &&
      isEffectiveOnPropertyTrack,
  });

  // Whether we have a definitive answer about this journey's tracks.
  // For statuses that aren't effectiveOnPropertyTrack, tracks are irrelevant
  // so we're always "ready". Otherwise we only trust an empty result when the
  // fetch has actually succeeded and isn't mid-flight - a failed or pending
  // fetch must NOT be mistaken for "no tracks exist".
  const tracksReady =
    !isEffectiveOnPropertyTrack || (isTracksSuccess && !isFetchingTracks);

  // Reason the Save button is blocked on track loading, if any (drives the
  // inline message in the form). null when nothing is blocking.
  const trackBlockReason: "loading" | "error" | null = !isEffectiveOnPropertyTrack
    ? null
    : isTracksError
      ? "error"
      : !tracksReady
        ? "loading"
        : null;

  // Disable Save while the track state is indeterminate (loading or errored).
  // We intentionally do NOT disable for the "multiple tracks, none selected"
  // case - that path opens the selection modal when Save is clicked.
  const disableSaveForTrack = isEffectiveOnPropertyTrack && !tracksReady;

  // Handle track selection when modal opens
  useEffect(() => {
    if (
      status.editLeadStatusModalOpen &&
      tracksData?.data?.results &&
      isEffectiveOnPropertyTrack
    ) {
      const tracks = tracksData.data.results;

      if (tracks.length === 1) {
        // Auto-select if only one track
        setSelectedTrackUId(tracks[0].uid);
        setShowTrackSelectionModal(false);
      } else if (tracks.length > 1) {
        // Don't auto-select, user will need to choose when submitting
        // Keep selectedTrackUId as null so modal will show on submit
        setSelectedTrackUId(null);
      } else {
        // No tracks found
        setSelectedTrackUId(null);
        setShowTrackSelectionModal(false);
      }
    } else if (!status.editLeadStatusModalOpen) {
      // Reset when modal closes
      setSelectedTrackUId(null);
      setShowTrackSelectionModal(false);
      setPendingSubmit(null);
    }
  }, [
    status.editLeadStatusModalOpen,
    tracksData,
    status.state,
    status.data,
    isEffectiveOnPropertyTrack,
  ]);

  // Reset track selection when status changes and effectiveOnPropertyTrack is false
  useEffect(() => {
    if (!isEffectiveOnPropertyTrack) {
      setSelectedTrackUId(null);
      setShowTrackSelectionModal(false);
      setPendingSubmit(null);
    }
  }, [isEffectiveOnPropertyTrack, status.state]);


  const handleErealtyIframeConfirm = async (payload?: any) => {

    const journeyStatusPayload = payload || {
      statusUId: selectedStatus.uid,
      erealtyForm: true,
      ...(erealtyIframeComment && { comment: erealtyIframeComment }),
      erealtyFormData: transformErealtyPayload(erealtyIframePayload),
    };
 
    await handleConfirmUpdate?.({
        journeyStatus: journeyStatusPayload,
      });
      
 
     const queries = queryClient.getQueriesData({ 
      queryKey: ["lead-activity"], 
      exact: false 
     });
    let formSubmissionTimestamp;
    let statusUpdateUid;

    
     if (queries.length > 0) {
       const activityData = queries[0][1] as any;
       // Get the first matching query's data
       statusUpdateUid = activityData?.pages?.[0]?.data?.items?.[0]?.uid;
       const createdAt = activityData?.pages?.[0]?.data?.items?.[0]?.createdAt;
       // Ensure the timestamp is in UTC ISO format
       formSubmissionTimestamp = createdAt ? new Date(createdAt).toISOString() : undefined;
     }
      
       let timeBetweenShowResultsAndSubmit: number | undefined;
      if (
        showResultsEventTimestampRef.current !== null &&
        filtersSubmitEventTimestampRef.current !== null &&
        showResultsEventTimestampRef.current < filtersSubmitEventTimestampRef.current
      ) {
        // Event timestamps are in milliseconds, convert to seconds
        timeBetweenShowResultsAndSubmit = 
          (filtersSubmitEventTimestampRef.current - showResultsEventTimestampRef.current) / 1000;
      }

      // Track the submission with all analytics data
      MixpanelTrackingUtil.trackErealtyPresentationFiltersSubmit(
        analytics,
        erealtyIframePayload,
        userInfo,
        {
          clicked_show_results: clickedShowResults,
          time_between_show_results_and_submit_form: timeBetweenShowResultsAndSubmit,
          status_update_uid: statusUpdateUid,
          form_submission_timestamp: formSubmissionTimestamp || new Date().toISOString(),
        }
      );

      // Reset tracking state after submission
      showResultsEventTimestampRef.current = null;
      filtersSubmitEventTimestampRef.current = null;
      setClickedShowResults(false);
 
    
  };

  const proceedWithSubmit = async (
    values: any,
    formType?: "form" | "sale",
    editedSaleData?: {
      saleUId: string;
      leadUId: string;
    }
  ) => {
    const totalFileSize = files.reduce((acc, file) => acc + file.size, 0);
    const totalPaymentFileSize = paymentFiles.reduce(
      (acc, file) => acc + file.size,
      0
    );
    const totalPaymentPlanFileSize = paymentPlanFiles.reduce(
      (acc, file) => acc + file.size,
      0
    );

    const filesSizeValidation =
      totalFileSize > MAX_TOTAL_SIZE_BYTES ||
      totalPaymentFileSize > MAX_TOTAL_SIZE_BYTES ||
      totalPaymentPlanFileSize > MAX_TOTAL_SIZE_BYTES;

    if (filesSizeValidation) {
      showToast({
        type: ToastType.ERROR,
        message: `Total documents sizes exceeds ${MAX_TOTAL_SIZE_MB}MB. Please upload smaller files.`,
        toastId: TOAST_IDS.UPDATE_SALE_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
      return;
    }

    try {
      const formData = transformFormData(
        values,
        formFields,
        erealtyIframePayload
      );
      const attachments: File[] = [];
      const attachmentMetadata: AttachmentMetadata[] = [];
      if (values.recordings && Array.isArray(values.recordings)) {
        for (let i = 0; i < values.recordings.length; i++) {
          const recording = values.recordings[i];
          if (recording.audioBlob) {
            const file = new File(
              [recording.audioBlob],
              `voice-recording-${i + 1}.mp4`,
              { type: recording.audioBlob.type || "audio/mp4" }
            );
            if (file.size <= MAX_TOTAL_SIZE_BYTES) {
              attachments.push(file);
              const duration = recording.duration || 0;
              attachmentMetadata.push({ duration });
            }
          }
        }
      }

      if (attachments.length > MAX_ATTACHMENT_FILES) {
        showToast({
          type: ToastType.ERROR,
          message: `Maximum ${MAX_ATTACHMENT_FILES} attachments allowed`,
          toastId: TOAST_IDS.UPDATE_SALE_ERROR,
          options: { position: "bottom-left", autoClose: 3000 },
        });
        return;
      }

      const journeyStatusPayload: any = {
        statusUId: status.state,
        ...(attachments.length > 0 && { attachments }),
        ...(attachments.length > 0 && { attachmentMetadata }),
        ...(values.comment && { comment: values.comment }),
        formData: formType === "form" ? formData : undefined,
      };

      // Check if the selected status has effectiveOnPropertyTrack enabled
      const selectedStatus = status.data?.data?.find(
        (s: any) => s.uid === status.state
      );
      const isEffectiveOnPropertyTrack =
        selectedStatus?.effectiveOnPropertyTrack === true;

      // Resolve trackUId for statuses that are effective on a property track.
      if (isEffectiveOnPropertyTrack) {
        // Never submit while the track fetch is indeterminate: a pending or
        // failed fetch looks like "no tracks" but isn't, so submitting here
        // would drop a trackUId that should have been sent. Save is disabled
        // in this state; this is the defensive backstop.
        if (!tracksReady) {
          showToast({
            type: ToastType.ERROR,
            message:
              "Property tracks haven't finished loading. Please wait and try again.",
            toastId: TOAST_IDS.UPDATE_SALE_ERROR,
            options: { position: "bottom-left", autoClose: 3000 },
          });
          return;
        }

        const trackResults = tracksData?.data?.results || [];

        if (trackResults.length === 1) {
          // Exactly one track - auto-select it.
          journeyStatusPayload.trackUId = trackResults[0].uid;
        } else if (trackResults.length > 1) {
          // Multiple tracks - a selection is required. Without one we must not
          // submit (the selection modal is the path that supplies it).
          if (!selectedTrackUId) {
            return;
          }
          journeyStatusPayload.trackUId = selectedTrackUId;
        }
        // trackResults.length === 0 with a successful fetch means the journey
        // genuinely has no tracks - submitting without a trackUId is valid.
      }

      // Add createLeadTrackDto if createdTracks exist
      const tracksToCreate = createdTracks
        .filter((t) => t.developerUId)
        .map(({ leadUId, developerUId, developerUserUId }) => ({
          leadUId,
          developerUId,
          developerUserUId: developerUserUId || undefined,
        }));

      if (tracksToCreate.length > 0) {
        journeyStatusPayload.createDeveloperTrackDTO = tracksToCreate;
      }

      // Include sale data in the status update payload (both create and update)
      if (formType === "sale") {
        const saleDTO: Record<string, any> = {};
        Object.keys(values).forEach((key) => {
          if (!Array.isArray(values[key])) saleDTO[key] = values[key];
        });


        journeyStatusPayload.salesForm = true;
        journeyStatusPayload.saleDTO = saleDTO;
        journeyStatusPayload.saleFiles = files;
        journeyStatusPayload.salePaymentFiles = paymentFiles;
        journeyStatusPayload.salePaymentPlanFiles = paymentPlanFiles;

        if (status.state) saleDTO.newLeadStatusUId = status.state;
        if (editedSaleData) saleDTO.saleUId = editedSaleData.saleUId;
      }

      await handleConfirmUpdate?.({
        journeyStatus: journeyStatusPayload,
      });

      queryClient.invalidateQueries({ queryKey: ["getComments"] });
      queryClient.invalidateQueries({ queryKey: ["getStatuses"] });

      if (formType === "sale") {
        queryClient.invalidateQueries({ queryKey: ["getLeadSales"] });
        queryClient.invalidateQueries({ queryKey: ["getLead"] });
      }
    } catch (error) {
      console.error("Error submitting sale:", error);
    }
  };

  const handleSubmit = async (
    values: any,
    formType?: "form" | "sale",
    editedSaleData?: {
      saleUId: string;
      leadUId: string;
    }
  ) => {
    // Check if the selected status has effectiveOnPropertyTrack enabled
    const selectedStatus = status.data?.data?.find(
      (s: any) => s.uid === status.state
    );
    const isEffectiveOnPropertyTrack =
      selectedStatus?.effectiveOnPropertyTrack === true;

    // Don't submit while the track state is indeterminate. Save is disabled in
    // this case, but guard here too so a stale click or programmatic call can't
    // slip a status update through without its required trackUId.
    if (isEffectiveOnPropertyTrack && !tracksReady) {
      return;
    }

    // Get tracks from the data structure - handle both possible structures
    // The query returns: { data: { results: [...] } }
    const tracks = tracksData?.data?.results || [];

    // Check if we need track selection
    // Only show modal if:
    // 1. Status has effectiveOnPropertyTrack enabled
    // 2. There are multiple tracks
    // 3. No track is selected yet
    if (isEffectiveOnPropertyTrack && tracks.length > 1 && !selectedTrackUId) {
      // Show modal to select track
      setPendingSubmit({ values, formType, editedSaleData });
      setShowTrackSelectionModal(true);
      return;
    }

    // Proceed with submission
    await proceedWithSubmit(values, formType, editedSaleData);
  };

  const handleTrackSelectionConfirm = async () => {
    if (pendingSubmit && selectedTrackUId) {
      setShowTrackSelectionModal(false);
      await proceedWithSubmit(
        pendingSubmit.values,
        pendingSubmit.formType,
        pendingSubmit.editedSaleData
      );
      setPendingSubmit(null);
    }
  };

  const saleTypeMap = useMemo(
    () =>
      new Map<string, SaleType>([
        [normalizeStatusName(SalesStatuses.UNIT_BLOCKED), SaleTypeNames.Blocked],
        [
          normalizeStatusName(SalesStatuses.UNIT_RESERVED),
          SaleTypeNames.Reserved,
        ],
        [normalizeStatusName(SalesStatuses.EOI), SaleTypeNames.EOI],
        [
          normalizeStatusName(SalesStatuses.CONGRATS_ITS_A_SALE),
          SaleTypeNames.Contracted,
        ],
        [
          normalizeStatusName(SalesStatuses.PROMISE_SALE),
          SaleTypeNames.Promise,
        ],
      ]),

    []
  );

  const getSaleType = (status: string): SaleType =>
    saleTypeMap.get(normalizeStatusName(status)) || null;

  const handleFiles = useCallback((newFiles: File | File[]) => {
    setFiles((prevFiles) => {
      const currentFiles = Array.isArray(prevFiles) ? prevFiles : [];
      const newFilesArray = Array.isArray(newFiles) ? newFiles : [newFiles];
      const combinedFiles = [...currentFiles];
      newFilesArray.forEach((newFile) => {
        if (!currentFiles.some((file) => file.name === newFile.name)) {
          combinedFiles.push(newFile);
        }
      });

      return combinedFiles;
    });
  }, []);

  const handlePaymentFiles = useCallback((newFiles: File | File[]) => {
    setPaymentPlanFiles([]);
    setPaymentFiles((prevFiles) => {
      const currentFiles = Array.isArray(prevFiles) ? prevFiles : [];
      const newFilesArray = Array.isArray(newFiles) ? newFiles : [newFiles];
      const combinedFiles = [...currentFiles];
      newFilesArray.forEach((newFile) => {
        if (!currentFiles.some((file) => file.name === newFile.name)) {
          combinedFiles.push(newFile);
        }
      });

      return combinedFiles;
    });
  }, []);

  const handlePaymentPlanFiles = useCallback((newFiles: File | File[]) => {
    setPaymentFiles([]);

    setPaymentPlanFiles((prevFiles) => {
      const currentFiles = Array.isArray(prevFiles) ? prevFiles : [];
      const newFilesArray = Array.isArray(newFiles) ? newFiles : [newFiles];
      const combinedFiles = [...currentFiles];
      newFilesArray.forEach((newFile) => {
        if (!currentFiles.some((file) => file.name === newFile.name)) {
          combinedFiles.push(newFile);
        }
      });

      return combinedFiles;
    });
  }, []);

  const handleDeletePaymentFiles = useCallback(
    (fileToDelete?: UploadedFile) => {
      setPaymentFiles((prevFiles) =>
        prevFiles.filter((file) => file.name !== fileToDelete?.name)
      );

      setPaymentPlanFiles((prevFiles) =>
        prevFiles.filter((file) => file.name !== fileToDelete?.name)
      );
    },
    []
  );

  const handleDeleteFile = useCallback((fileToDelete: UploadedFile) => {
    setFiles((prevFiles) =>
      prevFiles.filter((file) => file.name !== fileToDelete.name)
    );
  }, []);

  return {
    formFields,
    isLoadingForm: queryIsLoading && fetchStatus !== "idle",
    handleSubmit,
    getSaleType,
    setStatusFormUId,
    handleFiles,
    handlePaymentFiles,
    handlePaymentPlanFiles,
    handleDeletePaymentFiles,
    handleDeleteFile,
    files,
    paymentFiles,
    paymentPlanFiles,
    // Track selection related
    tracks: tracksData?.data?.results || [],
    isLoadingTracks,
    disableSaveForTrack,
    trackBlockReason,
    refetchTracks,
    selectedTrackUId,
    setSelectedTrackUId,
    selectedStatus,
    showTrackSelectionModal,
    setShowTrackSelectionModal,
    handleTrackSelectionConfirm,
    // E-Realty iframe related
    erealtyIframeOpenCommentModal,
    setErealtyIframeOpenCommentModal,
    erealtyIframeComment,
    setErealtyIframeComment,
    erealtyIframePayload,
    setErealtyIframePayload,
    handleErealtyIframeConfirm,
    createdTracks,
    developers,
    isLoadingDevelopers,
    setSearchDevelopers,
    handleAddTrack,
    handleDeleteTrack,
    handleTrackChange,
  };
};

export default useEditStatusForm;
